import { useState } from "react";

import { ButtonWrapper } from "app/components/React/WidgetContainer/styledComponents";
import { chosenSites } from "app/common/services/chosenSites";
import { CompareTabsContainer } from "app/components/compareTabs/CompareTabsContainer";
import DownloadButtonMenu from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { AdIntelligenceController } from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import { FIRST_PAGE, adDownloadLimits } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterStateWithName } from "single-spa/react-app/router/IRouterState";

import { WEBSITE_SORT_OPTIONS } from "./adsSortOptions";
import { AdsDownloadDropdown } from "./components/ads-download-dropdown/AdsDownloadDropdown";
import { AdIntelligenceAdsFilters } from "./filters/AdIntelligenceAdsFilters";
import { DownloadProgressModal } from "./modal/DownloadProgressModal";
import type { SortType } from "./hooks/useAdIntelligenceAdsSortDropdown";
import { useAdIntelligenceDownloadAds } from "./hooks/useAdIntelligenceDownloadAds";
import { useAdsGallery } from "./hooks/useAdsGallery";
import { AdsGallery } from "./AdsGallery";

const NUMBER_OF_ADS_PER_PAGE = 100;

interface IWebsiteAdsProps {
    controller: AdIntelligenceController;
}

export const WebsiteAds = ({ controller }: IWebsiteAdsProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>(params.selectedCampaigns ?? []);
    const [selectedEntity, setSelectedEntity] = useState<string[]>(
        controller === "advertiser" ? (params.selectedPublishers ?? []) : (params.selectedAdvertisers ?? []),
    );
    const [currentPage, setCurrentPage] = useState<number>(FIRST_PAGE);
    const [sort, setSort] = useState<SortType>((params.sort as SortType) || "lastseen");
    const [selectedFormat, setSelectedFormat] = useState<string | undefined>(params?.format);

    const {
        data = [],
        isLoading,
        isError,
        startDate,
        totalValidCount,
        isFromNewScraper,
    } = useAdsGallery(
        controller,
        currentPage,
        NUMBER_OF_ADS_PER_PAGE,
        sort,
        controller === "advertiser" ? selectedCampaigns : undefined,
        selectedFormat,
        selectedEntity,
    );
    const totalCount = totalValidCount ?? 0;

    const isCompare = chosenSites.isCompare();

    const {
        downloadButtonProps,
        downloadImagesAsZip,
        isDownloading,
        isDownloadModalOpen,
        downloadProgress,
        cancelDownload,
    } = useAdIntelligenceDownloadAds({
        data,
        currentPage,
        totalCount,
        itemsPerPage: NUMBER_OF_ADS_PER_PAGE,
        onNextPage: () => setCurrentPage((prev) => prev + 1),
    });
    const { rule: adDownloadRule, value: adDownloadValue } = adDownloadLimits();

    const resetPage = () => {
        setCurrentPage(FIRST_PAGE);
    };

    const onSelectSite = (selectedSite: string) => {
        allTrackers.dispatchTrackEvent("Tab", "click", `Filter Domain/${selectedSite}`);
        resetPage();

        if (controller === "advertiser") {
            setSelectedCampaigns([]);
        }
        setSelectedEntity([]);

        void proNavigator.go((proNavigator.current() as IRouterStateWithName).name, {
            ...proNavigator.getParams(),
            adsSelectedSite: selectedSite,
        });
    };

    const handleCampaignsFilterChange = (campaigns: string[]) => {
        setSelectedCampaigns(campaigns);
        resetPage();
    };

    const handleEntityFilterChange = (entities: string[]) => {
        setSelectedEntity(entities);
        resetPage();
    };

    const handleFormatFilterChange = (format: string) => {
        setSelectedFormat(format);
        resetPage();
        proNavigator.updateQueryParams(
            {
                format: format,
            },
            { reload: false },
        );
    };

    const compareTabs = isCompare ? (
        <CompareTabsContainer
            onSelectSite={onSelectSite}
            keyName="adsFirstLoads"
            domainQueryParamName="adsSelectedSite"
            hideResults={true}
        />
    ) : null;

    const filters = (
        <AdIntelligenceAdsFilters
            selectedCampaigns={controller === "advertiser" ? selectedCampaigns : undefined}
            onCampaignsFilterChange={controller === "advertiser" ? handleCampaignsFilterChange : undefined}
            onEntityFilterChange={handleEntityFilterChange}
            selectedEntity={selectedEntity}
            controller={controller}
            selectedFormat={selectedFormat}
            onFormatFilterChange={handleFormatFilterChange}
            isFromNewScraper={isFromNewScraper}
        />
    );

    const download =
        adDownloadRule === "Hidden" ? null : (
            <ButtonWrapper>
                {adDownloadRule === "Limited" ? (
                    <AdsDownloadDropdown
                        totalCount={totalCount}
                        onDownloadAds={() => void downloadImagesAsZip(adDownloadValue)}
                        isLoading={isDownloading}
                        dataAutomationPrefix="display-ads-download"
                    />
                ) : (
                    <DownloadButtonMenu
                        PNG
                        handleDownloadFunction={() => downloadImagesAsZip()}
                        {...downloadButtonProps}
                    />
                )}
            </ButtonWrapper>
        );

    return (
        <>
            <AdsGallery
                data={data}
                isLoading={isLoading}
                isError={isError}
                totalCount={totalCount}
                controller={controller}
                sortOptions={WEBSITE_SORT_OPTIONS}
                selectedSort={sort}
                onSortChange={(id) => {
                    setSort(id as SortType);
                    setCurrentPage(FIRST_PAGE);
                }}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                titleLabelKey="ad.intelligence.advertiser.ad.widget.title"
                scraperBannerDate={startDate ?? undefined}
                filtersSlot={filters}
                compareTabsSlot={compareTabs}
                downloadSlot={download}
            />
            <DownloadProgressModal
                isOpen={isDownloadModalOpen}
                isDownloading={isDownloading}
                progress={downloadProgress}
                onCancel={cancelDownload}
            />
        </>
    );
};
